/**
 * Revisions Hooks
 * Custom hooks for fetching and managing content revisions.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ApiResponse } from '@/lib/api';

// Helper to build query string manually to match reference API format
// This avoids aggressive URL encoding that qs/axios might do
function buildQueryString(params: Record<string, any>): string {
    const parts: string[] = [];

    // Handle fields array
    if (params.fields && Array.isArray(params.fields)) {
        for (const field of params.fields) {
            parts.push(`fields[]=${encodeURIComponent(field)}`);
        }
    }

    // Handle sort
    if (params.sort) {
        parts.push(`sort=${encodeURIComponent(params.sort)}`);
    }

    // Handle aggregate
    if (params.aggregate) {
        for (const [key, value] of Object.entries(params.aggregate)) {
            if (Array.isArray(value)) {
                for (const v of value) {
                    parts.push(`aggregate[${key}][]=${encodeURIComponent(v)}`);
                }
            } else {
                parts.push(`aggregate[${key}]=${encodeURIComponent(value as string)}`);
            }
        }
    }

    // Handle filter
    if (params.filter) {
        parts.push(...buildFilterParams(params.filter, 'filter'));
    }

    return parts.length > 0 ? `?${parts.join('&')}` : '';
}

function buildFilterParams(filter: Record<string, any>, prefix: string): string[] {
    const parts: string[] = [];

    for (const [key, value] of Object.entries(filter)) {
        const paramKey = `${prefix}[${key}]`;

        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
            parts.push(...buildFilterParams(value, paramKey));
        } else if (Array.isArray(value)) {
            for (let i = 0; i < value.length; i++) {
                const item = value[i];
                if (typeof item === 'object' && item !== null) {
                    parts.push(...buildFilterParams(item, `${paramKey}[${i}]`));
                } else {
                    parts.push(`${paramKey}[${i}]=${encodeURIComponent(item)}`);
                }
            }
        } else {
            parts.push(`${paramKey}=${encodeURIComponent(value)}`);
        }
    }

    return parts;
}

// Types
export interface RevisionActivity {
    id: number;
    action: string;
    user: {
        id: string;
        first_name: string | null;
        last_name: string | null;
        email: string;
        avatar: string | null;
    } | string | null;
    timestamp: string;
    ip: string | null;
    user_agent: string | null;
    collection: string;
    item: string;
    comment: string | null;
}

export interface Revision {
    id: number;
    activity: RevisionActivity | number;
    collection: string;
    item: string;
    data: Record<string, any> | null;
    delta: Record<string, any> | null;
    parent: number | null;
    version: string | null;
}

// Query keys
export const revisionKeys = {
    all: ['revisions'] as const,
    list: (filters: Record<string, any>) => [...revisionKeys.all, 'list', filters] as const,
    forItem: (collection: string, item: string) => [...revisionKeys.all, 'item', collection, item] as const,
    detail: (id: number | string) => [...revisionKeys.all, 'detail', id] as const,
};

/**
 * Fetch revisions for a specific item
 */
export function useItemRevisions(collection: string, item: string, options?: { enabled?: boolean }) {
    return useQuery({
        queryKey: revisionKeys.forItem(collection, item),
        queryFn: async (): Promise<Revision[]> => {
            const queryString = `?filter[collection][_eq]=${encodeURIComponent(collection)}&filter[item][_eq]=${encodeURIComponent(item)}&sort[]=-id&limit=100`;
            const response = await api.get<ApiResponse<Revision[]>>(`/revisions${queryString}`);
            return response.data;
        },
        enabled: options?.enabled ?? (!!collection && !!item),
    });
}

/**
 * Fetch a single revision by ID
 */
export function useRevision(id: number | string, options?: { enabled?: boolean }) {
    return useQuery({
        queryKey: revisionKeys.detail(id),
        queryFn: async (): Promise<Revision> => {
            const response = await api.get<ApiResponse<Revision>>(`/revisions/${id}`);
            return response.data;
        },
        enabled: options?.enabled ?? !!id,
    });
}

/**
 * Revert to a specific revision
 */
export function useRevertRevision() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (revisionId: number | string) => {
            const response = await api.post<ApiResponse<{ success: boolean }>>(`/revisions/${revisionId}/revert`);
            return response.data;
        },
        onSuccess: () => {
            // Invalidate the item data and revisions
            queryClient.invalidateQueries({ queryKey: ['items'] });
            queryClient.invalidateQueries({ queryKey: revisionKeys.all });
        },
    });
}

/**
 * Helper to get user display name from revision activity
 */
export function getRevisionUserName(activity: RevisionActivity | number): string {
    if (typeof activity === 'number') {
        return 'Unknown';
    }

    const user = activity.user;
    if (!user) return 'System';
    if (typeof user === 'string') return user;

    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || user.email || 'Unknown';
}

/**
 * Helper to format revision timestamp
 */
export function formatRevisionTime(timestamp: string): { relative: string; absolute: string } {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();

    // Relative time
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    let relative: string;
    if (seconds < 60) {
        relative = 'Just now';
    } else if (minutes < 60) {
        relative = `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else if (hours < 24) {
        relative = `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else if (days < 7) {
        relative = `${days} day${days !== 1 ? 's' : ''} ago`;
    } else {
        relative = date.toLocaleDateString();
    }

    // Absolute time
    const absolute = date.toLocaleString();

    return { relative, absolute };
}

/**
 * Helper to count changes in delta
 */
export function countDeltaChanges(delta: Record<string, any> | null): number {
    if (!delta) return 0;
    return Object.keys(delta).length;
}

/**
 * Helper to get action label
 */
export function getActionLabel(action: string): string {
    switch (action) {
        case 'create':
            return 'Created';
        case 'update':
            return 'Updated';
        case 'delete':
            return 'Deleted';
        case 'revert':
            return 'Reverted';
        default:
            return action;
    }
}

/**
 * Group revisions by date
 */
export function groupRevisionsByDate(revisions: Revision[]): Map<string, Revision[]> {
    const groups = new Map<string, Revision[]>();

    for (const revision of revisions) {
        const activity = revision.activity as RevisionActivity;
        if (typeof activity === 'number') continue;

        const date = new Date(activity.timestamp);
        const dateKey = date.toLocaleDateString();

        if (!groups.has(dateKey)) {
            groups.set(dateKey, []);
        }
        groups.get(dateKey)!.push(revision);
    }

    return groups;
}

/**
 * Fetch revision count for a specific item
 */
export function useItemRevisionsCount(collection: string, item: string) {
    return useQuery({
        queryKey: [...revisionKeys.forItem(collection, item), 'count'],
        queryFn: async () => {
            const queryString = buildQueryString({
                filter: {
                    collection: { _eq: collection },
                    item: { _eq: item },
                },
                aggregate: {
                    count: ['id'],
                },
            });

            const response = await api.get<{ data: [{ count: { id: string } }] }>(`/revisions${queryString}`);

            // Handle potential empty response or missing data
            if (response.data && response.data[0] && response.data[0].count) {
                return Number(response.data[0].count.id);
            }
            return 0;
        },
        enabled: !!collection && !!item,
    });
}